/*
 * ⚠️  WARNING: BRIDGE LOGIC - DO NOT MODIFY WITHOUT #team-frontend-tech APPROVAL ⚠️
 *
 * This file contains critical bridge logic that enables communication between the
 * Ember app (apps/embercom) and React app (apps/teammate-app). Any modifications
 * to this file can break the integration between the two applications.
 *
 * Before making any changes to this file, please reach out to #team-frontend-tech
 * for review and approval.
 */

import { isRenderedAsIsland } from '@/utils/routing';
import type { LocaleType } from '@/utils/locale';
import type { AnalyticsData } from '@/utils/analytics';
import { showToastInDevelopmentEnvironmentOnly } from '@/utils/dev-toast';
import { ThemeType } from '@/utils/theme';
import { debugConsole } from '@/utils/debug-console';

//-Notification events---------------------------------------------------------------------------------------------------

export type NotificationType =
  | 'default'
  | 'confirmation'
  | 'help'
  | 'error'
  | 'warning'
  | 'loading';

export type NotificationEventType = `notification:${NotificationType}`;

export interface NotificationEventPayload {
  // The message to display
  message: string;
  // The duration to display the notification
  duration?: number;
}

/**
 * A mapping of the notification events and their payloads
 */
export type NotificationEvent = Record<NotificationEventType, NotificationEventPayload | undefined>;

//-Snackbar events (inbox-only)------------------------------------------------------------------------------------------

export type SnackbarType = 'default' | 'error' | 'help' | 'warning' | 'confirmation';

export interface SnackbarEventPayload {
  message: string;
  type?: SnackbarType;
  persistent?: boolean;
  clearable?: boolean;
  timeout?: number;
}

export interface SnackbarEvent {
  'snackbar:notify': SnackbarEventPayload;
}

//-Routing events-------------------------------------------------------------------------------------------------------

export interface RouteChangeEventPayload {
  pathname: string;
  search?: string;
}

interface NavigationRequestPayload {
  to: string;
  appState: AppStateEventPayload;
}

export interface PaywallEventPayload {
  planId?: number;
  featureName?: string;
  showUpgradePathPlans?: boolean;
  featureLessHeaders?: boolean;
  onActionComplete?: ({ success }: { success: boolean }) => Promise<void>;
}

/**
 * A mapping of the routing events and their payloads
 */
export interface RouterEvent {
  // The router has booted
  'route:boot': null;
  // The react-app route has changed
  'route:change': RouteChangeEventPayload;
  // the host app is requesting a navigation
  'route:navigation-request': NavigationRequestPayload;
}

//-App state events-----------------------------------------------------------------------------------------------------

export interface AppStateEventPayload {
  locale: LocaleType;
  navbarCollapsed: boolean;
  theme: ThemeType;
}
export interface AppStateEvent {
  'app:state-change': AppStateEventPayload;
}

//-Modal events-----------------------------------------------------------------------------------------------------
export type ModalType =
  | 'web-push-notifications-permission-prompt'
  | 'web-push-notifications-permission-denied'
  | 'permission-request';

export interface ModalEventPayload {
  name: ModalType;
  data: Record<string, unknown>;
  options: Record<string, unknown>;
}
export interface ModalEvent {
  'modal:open': ModalEventPayload;
  'modal:close': null;
}

export interface AnalyticsEvent {
  'analytics:track': AnalyticsData;
}

export interface MetricsData {
  increment?: string[];
  decrement?: string[];
  tags?: Record<string, unknown>;
}

export interface MetricsEvent {
  'metrics:capture': MetricsData;
}

export interface PaywallEvent {
  'paywall:open': PaywallEventPayload;
}

export interface PermissionRequestEventPayload {
  permissionRequired: string;
}

export interface PermissionRequestEvent {
  'permissionsModal:open': PermissionRequestEventPayload;
}

//-Teammate app events--------------------------------------------------------------------------------------------------

export interface TeammateAppEventPayload {
  /**
   * A unique identifier for the type of teammate app event (e.g., 'ai-categories', 'usage-reminders')
   */
  identifier: string;
  /**
   * An object containing key-value pairs representing the data for the teammate app event.
   * The keys are strings and the values can be of any type.
   *
   * Note: Using 'unknown' instead of 'any' for better type safety.
   */
  data: Record<string, unknown>;
}

export interface TeammateAppEvent {
  'teammate-app:event': TeammateAppEventPayload;
}

//-Mounting point events-------------------------------------------------------------------------------------------------

export interface MountingPointInsertedEventPayload {
  /** Identifier of the mounting point that was inserted */
  identifier: string;
}

export interface MountingPointEvent {
  'mounting-point:inserted': MountingPointInsertedEventPayload;
}

//-Bridge events--------------------------------------------------------------------------------------------------------
/**
 * A mapping of the bridge events and their payloads. use null if the event has no payload
 * */
export interface BridgeEventsPayloadMapping
  extends NotificationEvent,
    SnackbarEvent,
    RouterEvent,
    AppStateEvent,
    ModalEvent,
    AnalyticsEvent,
    MetricsEvent,
    PaywallEvent,
    PermissionRequestEvent,
    TeammateAppEvent,
    MountingPointEvent {
  'navbar:toggle': null;
  'navbar:collapse': null;
}

export type BridgeEventKeys = keyof BridgeEventsPayloadMapping;

/**
 * A mapping of the bridge events with their corresponding "MessageEvent.data" types
 */
export type BridgeEventMessageDataMapping = {
  [K in BridgeEventKeys]: { type: K; payload: BridgeEventsPayloadMapping[K]; bridge?: boolean };
};

/**
 * Extend the MessageEvent interface to include the bridge events
 */
export type WindowBridgeMessageEvent = MessageEvent<
  BridgeEventMessageDataMapping[keyof BridgeEventMessageDataMapping]
>;

export type BridgeEventSubscriber<T extends BridgeEventKeys = BridgeEventKeys> = (
  args: BridgeEventMessageDataMapping[T],
) => void;

//-Bridge utilities-----------------------------------------------------------------------------------------------------

export const bridge = new EventTarget();

interface BridgeEventDetail<T> {
  type: keyof BridgeEventsPayloadMapping;
  payload: T;
}

/**
 * Dispatch a message to the parent app using the bridge.
 * e.g. dispatchBridgeEvent("navbar:toggle", {...})
 * e.g. dispatchBridgeEvent("notification:success", {...})
 * @param type - The event type
 * @param payload - The event payload
 */
export function dispatchBridgeEvent<T extends keyof BridgeEventsPayloadMapping>(
  type: T,
  payload?: BridgeEventsPayloadMapping[T],
) {
  const event = new CustomEvent<BridgeEventDetail<typeof payload>>('ember:bridge', {
    detail: { type, payload },
  });

  if (import.meta.env.DEV && !isRenderedAsIsland()) {
    showToastInDevelopmentEnvironmentOnly(
      `🌉 Sending bridge event: ${type}. See console for details`,
    );
  }

  debugConsole.log(`Bridge event sent: ${type}`, payload);
  bridge.dispatchEvent(event);
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Subscribe to bridge events from the host app.
 * @param cb - The callback to be called when a bridge event is received
 * @returns A function to unsubscribe from the bridge events.
 */

export function addBridgeMessageListener(cb: BridgeEventSubscriber) {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<BridgeEventDetail<unknown>>;
    const { type, payload } = customEvent.detail;

    cb({
      type,
      payload: payload as BridgeEventsPayloadMapping[typeof type],
      bridge: true,
    } as BridgeEventMessageDataMapping[keyof BridgeEventMessageDataMapping]);
  };

  bridge.addEventListener('react:bridge', handler);

  return () => {
    bridge.removeEventListener('react:bridge', handler);
  };
}

/**
 * Dispatch a teammate app event to notify Ember that a teammate app event has occurred.
 * This allows Ember code to react to data mutations that happen in React.
 *
 * @param identifier - A unique identifier for the type of teammate app event (e.g., 'ai-categories', 'reporting:drill-in-chart')
 * @param data - An object containing key-value pairs representing the data for the teammate app event.
 * The keys are strings and the values can be of any type.
 *
 * @example
 * // Notify that AI categories have changed
 * dispatchTeammateAppEvent('ai-categories', data);
 */
export function dispatchTeammateAppEvent(
  identifier: string,
  data: Record<string, unknown> = {},
): void {
  dispatchBridgeEvent('teammate-app:event', { identifier, data });
}
